/**
 * Canonical Big 5 and LATAM league preset membership for SE-4780.
 * @module features/player-filter/utils/leaguePresets
 */

import type { FilterOption } from "@/types/filter";

/** Supported league preset identifiers. */
export type LeaguePreset = "big5" | "latam";

/** League option enriched with the raw backend taxonomy fields. */
export interface LeaguePresetOption extends FilterOption {
  readonly country?: string | null;
  readonly leagueName?: string;
}

/**
 * Big 5 membership, keyed by country plus the domestic league's curated name.
 *
 * `leagues` is an explicit allowlist of accepted names per country, never a
 * prefix or substring rule: the backend curates Germany's top flight as
 * "1. Bundesliga" (SE-6525), and its sibling tiers "2. Bundesliga" and
 * "3. Liga" must stay out of the preset. Country is always part of the key,
 * so Austria's separate "Bundesliga" cannot match either.
 */
const BIG_5 = [
  { countries: ["England", "ENG"], leagues: ["Premier League"] },
  { countries: ["France", "FRA"], leagues: ["Ligue 1"] },
  { countries: ["Germany", "GER"], leagues: ["Bundesliga", "1. Bundesliga"] },
  { countries: ["Italy", "ITA"], leagues: ["Serie A"] },
  { countries: ["Spain", "ESP"], leagues: ["La Liga"] },
] as const;

/** Exact product taxonomy supplied by Pietra Nicolodi in Jira comment 36846. */
export const LATAM_COUNTRIES = [
  "Argentina",
  "Belize",
  "Bolivia",
  "Brazil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Dominican Republic",
  "Ecuador",
  "El Salvador",
  "French Guiana",
  "Guadeloupe",
  "Guatemala",
  "Guyana",
  "Haiti",
  "Honduras",
  "Martinique",
  "Mexico",
  "Nicaragua",
  "Panama",
  "Paraguay",
  "Peru",
  "Puerto Rico",
  "Saint Martin",
  "Suriname",
  "Uruguay",
  "Venezuela",
] as const;

/**
 * Normalizes harmless backend casing, diacritic, and whitespace differences.
 * @param value - Raw taxonomy value
 * @returns Stable value used for preset comparisons
 */
export const normalizeLeagueTaxonomy = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLocaleLowerCase("en");

const LATAM_COUNTRY_CODES = [
  "ARG",
  "BLZ",
  "BOL",
  "BRA",
  "CHL",
  "COL",
  "CRI",
  "CUB",
  "DOM",
  "ECU",
  "SLV",
  "GUF",
  "GLP",
  "GTM",
  "GUY",
  "HTI",
  "HND",
  "MTQ",
  "MEX",
  "NIC",
  "PAN",
  "PRY",
  "PER",
  "PRI",
  "MAF",
  "SUR",
  "URY",
  "VEN",
] as const;
const LATAM_SET = new Set(
  [...LATAM_COUNTRIES, ...LATAM_COUNTRY_CODES].map(normalizeLeagueTaxonomy)
);
const BIG_5_SET = new Set(
  BIG_5.flatMap(entry =>
    entry.countries.flatMap(country =>
      entry.leagues.map(
        league =>
          `${normalizeLeagueTaxonomy(country)}::${normalizeLeagueTaxonomy(league)}`
      )
    )
  )
);

/**
 * Returns the org-available league IDs belonging to a preset.
 * @param preset - Preset to evaluate
 * @param options - Organization-scoped league options
 * @returns Matching option IDs in source order
 */
export const getLeaguePresetIds = (
  preset: LeaguePreset,
  options: readonly LeaguePresetOption[]
): readonly string[] =>
  options
    .filter(option => {
      const country = normalizeLeagueTaxonomy(option.country ?? "");
      if (preset === "latam") return LATAM_SET.has(country);
      const league = normalizeLeagueTaxonomy(
        option.leagueName ?? option.label.split(" - ").at(-1) ?? option.label
      );
      return BIG_5_SET.has(`${country}::${league}`);
    })
    .map(option => option.id);

/**
 * Selects all available preset IDs, or deselects them when already selected.
 * @param selectedIds - Current league selection
 * @param presetIds - Organization-available IDs for the preset
 * @returns Updated immutable league selection
 */
export const toggleLeaguePreset = (
  selectedIds: readonly string[],
  presetIds: readonly string[]
): readonly string[] => {
  if (presetIds.length === 0) return selectedIds;
  const selected = new Set(selectedIds);
  const allSelected = presetIds.every(id => selected.has(id));
  if (allSelected) return selectedIds.filter(id => !presetIds.includes(id));
  return [...selectedIds, ...presetIds.filter(id => !selected.has(id))];
};
